import { H5, P } from "@/components/ui/Heading";
import { useLocale, useTranslations } from "next-intl";

import { CreateEditSearchAlertModal } from "@/components/SearchAlert/List/CreateModel";
import { Button } from "@/components/ui/Button";
import { Tooltip, TooltipContent } from "@/components/ui/Tooltip";
import { useSearch } from "@/contexts/SearchContext";
import { useSearchAlerts } from "@/hooks/useSearchAlert";
import { FetchPropertiesRequest } from "@/interfaces/property.interface";
import {
  convertPropertyFilterToState,
  convertStateToListingUrl,
} from "@/lib/property/listing/helpers";
import { getListingPageTitle } from "@/lib/property/listing/title";
import { formatNumber } from "@/lib/utils";
import { useAuth } from "@clerk/nextjs";
import { TooltipTrigger } from "@radix-ui/react-tooltip";
import { StarIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useToggle } from "usehooks-ts";

export interface ListTitleProps {
  total?: number;
}

export const ListTitle = ({ total }: ListTitleProps) => {
  const locale = useLocale();
  const { isSignedIn } = useAuth();

  const [isSavedSearch, setIsSavedSearch] = useState(false);

  const t = useTranslations("listing.page-title");
  const st = useTranslations("shared");
  const sat = useTranslations("search-alert");

  const [
    isSearchAlertModelOpen,
    toggleSearchAlertModel,
    setIsSearchAlertModelOpen,
  ] = useToggle();
  const {
    state,
    pageParams: { page, size },
    locations,
    listing,
    furnishing,
  } = useSearch();

  const { data: savedAlerts, isLoading } = useSearchAlerts();

  const title = useMemo(
    () =>
      getListingPageTitle({
        t,
        st,
        furnishing,
        listing,
        locations,
      }),
    [t, st, furnishing, listing, locale]
  );

  const makePaginationTitle = useCallback(() => {
    if (!total) {
      return "_";
    }

    let range = "";

    range = `${formatNumber((page - 1) * size + 1)}-${formatNumber(
      page * size > total ? total : page * size
    )}`;

    return `${t("viewing")} ${range} ${t("of")} ${t("properties", {
      value: formatNumber(total),
    })}`;
  }, [total, page, size]);

  // TODO: REFACTOR THIS LATER
  useEffect(() => {
    const handleCheck = async () => {
      const mappedSavedAlerts = await Promise.all(
        savedAlerts!.map(
          async (alert) =>
            await convertPropertyFilterToState(
              alert.filters as FetchPropertiesRequest,
              locale
            )
        )
      );

      const foundAlert = mappedSavedAlerts.find(
        (alertState) =>
          convertStateToListingUrl(state) ==
          convertStateToListingUrl(alertState)
      );

      if (foundAlert) {
        setIsSavedSearch(true);
      } else {
        setIsSavedSearch(false);
      }
    };

    if (savedAlerts && savedAlerts.length > 0) {
      handleCheck();
    }
  }, [savedAlerts, isLoading, JSON.stringify(state)]);

  return (
    <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-2">
      <div className="w-full flex flex-col gap-1.5 pt-2 px-1">
        <H5 as={"h1"} className="text-gray-800 line-clamp-2 font-bold">
          {title}
        </H5>
        <P className="-mt-2 text-gray-600">{makePaginationTitle()}</P>
      </div>

      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Button
            variant={"outline"}
            className="!px-5 w-full sm:w-fit"
            onClick={toggleSearchAlertModel}
            disabled={isSavedSearch || !isSignedIn}
          >
            <StarIcon className="size-4" />
            {isSavedSearch ? st("words.saved-search") : st("words.save-search")}
          </Button>
        </TooltipTrigger>
        {!isSignedIn && <TooltipContent>{sat("action.click")}</TooltipContent>}
      </Tooltip>

      <CreateEditSearchAlertModal
        isEdit={false}
        isOpen={isSearchAlertModelOpen}
        onClose={() => setIsSearchAlertModelOpen(false)}
        title={title}
      />
    </div>
  );
};
